import { useCallback, useMemo } from 'react';
import { NavigateOptions } from 'react-router-dom';

import { CloneParam } from '../common/queryParams/clone';
import { CreateParam } from '../common/queryParams/create';
import { getQueryParams, type QueryParams } from '../common/queryParams';
import { useQueryParams } from '../common/queryParams/useQueryParams';

export interface PanelParams {
  dataSourceId: number | CreateParam;
  dataSourceIndex: number;
  algorithmId: number | CreateParam;
  targetId: number;
  requiredKeyId: number | CreateParam | CloneParam;
  requiredKeyIndex: number;
  requiredKeyAlgorithmsId: number;
  requiredKeyAlgorithmsIndex: number;
  validationRuleId: number | CreateParam;
  validationRuleIndex: number;
  alternativeAlgorithmSetId: number | CreateParam | CloneParam;
  alternativeAlgorithmSetIndex: number | CloneParam;
}

const defaultPanelParams = {
  dataSourceId: undefined,
  dataSourceIndex: undefined,
  algorithmId: undefined,
  targetId: undefined,
  requiredKeyId: undefined,
  requiredKeyIndex: undefined,
  requiredKeyAlgorithmsId: undefined,
  requiredKeyAlgorithmsIndex: undefined,
  validationRuleId: undefined,
  validationRuleIndex: undefined,
  alternativeAlgorithmSetId: undefined,
  alternativeAlgorithmSetIndex: undefined,
} satisfies Record<keyof PanelParams, undefined>;

const panelParamNames = Object.keys(defaultPanelParams) as Array<
  keyof typeof defaultPanelParams
>;

export type DataObjectPanelNavigationParams = Partial<
  Pick<QueryParams, 'segment' | (typeof panelParamNames)[number]>
>;

export const getDataObjectPanelNavigationParams =
  (): DataObjectPanelNavigationParams => {
    const queryParams = getQueryParams();
    const paramNames = ['segment', ...panelParamNames] as const;

    return paramNames.reduce<DataObjectPanelNavigationParams>(
      (params, name) => {
        const value = queryParams[name];
        if (value !== undefined) {
          params[name] = value;
        }
        return params;
      },
      {},
    );
  };

export const usePanelParams = () => {
  const [
    {
      dataSourceId,
      dataSourceIndex,
      algorithmId,
      targetId,
      requiredKeyId,
      requiredKeyIndex,
      requiredKeyAlgorithmsId,
      requiredKeyAlgorithmsIndex,
      validationRuleId,
      validationRuleIndex,
      alternativeAlgorithmSetId,
      alternativeAlgorithmSetIndex,
    },
    setQueryParams,
  ] = useQueryParams<PanelParams>();

  const panelParams = useMemo(
    () => ({
      ...defaultPanelParams,
      dataSourceId,
      dataSourceIndex,
      algorithmId,
      targetId,
      requiredKeyId,
      requiredKeyIndex,
      requiredKeyAlgorithmsId,
      requiredKeyAlgorithmsIndex,
      validationRuleId,
      validationRuleIndex,
      alternativeAlgorithmSetId,
      alternativeAlgorithmSetIndex,
    }),
    [
      dataSourceId,
      dataSourceIndex,
      algorithmId,
      targetId,
      requiredKeyId,
      requiredKeyIndex,
      requiredKeyAlgorithmsId,
      requiredKeyAlgorithmsIndex,
      validationRuleId,
      validationRuleIndex,
      alternativeAlgorithmSetId,
      alternativeAlgorithmSetIndex,
    ],
  );

  const setPanelParams = useCallback(
    (panelParams?: Partial<PanelParams>, options?: NavigateOptions) => {
      setQueryParams({ ...defaultPanelParams, ...panelParams }, options);
    },
    [setQueryParams],
  );

  return [panelParams, setPanelParams] as const;
};
